import classNames from "classnames";
import { PropsWithChildren, useCallback } from "react";
import { FormattedMessage } from "react-intl";

import {
  BestSellingProductData,
  ErrorResponse,
  SalesDashboardData,
  SalesDashboardSummaryData,
  SalesDashboardTagsData,
  SalesDataTimePeriod,
} from "../../../data-store";
import useSalesDashboardRatingsData from "../../../data-store/useSalesDashboardRatingsData";
import useSalesDashboardSummaryData from "../../../data-store/useSalesDashboardSummaryData";
import useSalesDashboardTagsData from "../../../data-store/useSalesDashboardTagsData";
import { ImpactCategory } from "../../../domain/impactCategories";
import useImpactRatingInfos from "../../../services/useImpactRatingInfos";
import { ProportionsChartImpactRatingInfos } from "../../scope-3/ImpactRatingProportionsChart";
import StatusDisplay from "../../StatusDisplay";
import { Panel } from "../../utils/Panel";
import "../SalesDashboardPage.css";
import { useImpactCategory } from "../ImpactCategoryProvider";
import { ProductHighlights } from "../ProductHighlights";
import RatingDistributionBarChart from "../RatingDistributionBarChart";
import SalesAndAverageImpactByTags from "../SalesAndAverageImpactByTags";
import SalesImpactByTagsBarChart from "../SalesImpactByTagsBarChart";
import SalesSummaries from "../SalesSummaries";
import TimePeriodSelector from "../TimePeriodSelector";

const INFO_MAP: Record<ImpactCategory, EffectType> = {
  GHG: "GHG_PER_KG",
  LAND_USE: "LAND_USE_PER_KG",
  WATER_USE: "WATER_USE_PER_KG",
};

const UNITS_CONVERSION: Record<
  ImpactCategory,
  "ghgEmissionsUnit" | "landUseUnit" | "waterUseUnit"
> = {
  GHG: "ghgEmissionsUnit",
  LAND_USE: "landUseUnit",
  WATER_USE: "waterUseUnit",
};

type EffectType =
  | "GHG_PER_KG"
  | "GHG_PER_SERVING"
  | "LAND_USE_PER_KG"
  | "LAND_USE_PER_SERVING"
  | "WATER_USE_PER_KG"
  | "WATER_USE_PER_SERVING";

interface ChartsForTimePeriodProps {
  selectedTimePeriod: SalesDataTimePeriod | null;
  onTimePeriodChange: (period: SalesDataTimePeriod | null) => void;
}

const ChartsForTimePeriod: React.FC<
  PropsWithChildren<ChartsForTimePeriodProps>
> = ({ selectedTimePeriod, onTimePeriodChange }) => {
  const [salesDashboardRatingsDataStatus] =
    useSalesDashboardRatingsData(selectedTimePeriod);
  const [salesDashboardSummaryDataStatus] =
    useSalesDashboardSummaryData(selectedTimePeriod);
  const [salesDashboardTagsDataStatus] =
    useSalesDashboardTagsData(selectedTimePeriod);
  const { status: impactRatingInfosStatus } = useImpactRatingInfos();

  const { impactCategory } = useImpactCategory();
  const unit = UNITS_CONVERSION[impactCategory];

  const handleTimePeriodChange = useCallback(
    (period: SalesDataTimePeriod | null) => {
      onTimePeriodChange(period);
    },
    [onTimePeriodChange]
  );

  return (
    <>
      <TimePeriodSelector
        selectedPeriod={selectedTimePeriod}
        onPeriodChange={handleTimePeriodChange}
      />

      <div
        className={classNames(
          "SalesDashboardCard",
          "SalesDashboardCard_SummarySection",
          {
            SalesDashboardCard_loading:
              salesDashboardSummaryDataStatus.type === "loading",
          }
        )}
        style={{ padding: "16px 0px 20px 24px" }}
      >
        <StatusDisplay.Many<
          [
            SalesDashboardSummaryData | ErrorResponse | null,
            ProportionsChartImpactRatingInfos
          ]
        >
          statuses={[salesDashboardSummaryDataStatus, impactRatingInfosStatus]}
        >
          {(summary, { impactRatingInfos }) => {
            if (summary == null || impactRatingInfos == null) return null;
            if (summary.success === false) {
              if (summary.error.code === "CACHED_IMPACT_NOT_FOUND") {
                return (
                  <Panel>
                    <div className="p-5 text-center">
                      <FormattedMessage
                        id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                        defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                      />
                    </div>
                  </Panel>
                );
              }
            } else {
              return (
                <SalesSummaries
                  summary={summary}
                  impactRatingInfos={impactRatingInfos}
                />
              );
            }
          }}
        </StatusDisplay.Many>
      </div>

      <div className="SalesDashboard__RatingDistributionAndProductHighlights">
        <div
          className={classNames(
            "SalesDashboardCard",
            "SalesDashboardCard_RatingDistribution",
            {
              SalesDashboardCard_loading:
                salesDashboardRatingsDataStatus.type === "loading",
            }
          )}
          style={{ padding: "16px 0px 20px 24px" }}
        >
          <StatusDisplay.Many<
            [
              SalesDashboardData | ErrorResponse | null,
              ProportionsChartImpactRatingInfos
            ]
          >
            statuses={[
              salesDashboardRatingsDataStatus,
              impactRatingInfosStatus,
            ]}
          >
            {(ratingDist, { impactRatingInfos }) => {
              if (ratingDist == null || impactRatingInfos == null) return null;
              if (ratingDist.success === false) {
                if (ratingDist.error.code === "CACHED_IMPACT_NOT_FOUND") {
                  return (
                    <Panel>
                      <div className="p-5 text-center">
                        <FormattedMessage
                          id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                          defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                        />
                      </div>
                    </Panel>
                  );
                }
              } else {
                const effectType = INFO_MAP[impactCategory];
                const impactEntry = impactRatingInfos.find(
                  (r) => r.effectType === effectType
                )!;
                return (
                  <div>
                    <RatingDistributionBarChart
                      data={ratingDist}
                      impactRating={impactEntry}
                    />
                  </div>
                );
              }
            }}
          </StatusDisplay.Many>
        </div>

        <div
          className={classNames(
            "SalesDashboardCard",
            "SalesDashboardCard_ProductHighlights",
            "p-0",
            {
              SalesDashboardCard_loading:
                salesDashboardSummaryDataStatus.type === "loading",
            }
          )}
        >
          <StatusDisplay.Many<
            [SalesDashboardSummaryData | ErrorResponse | null]
          >
            statuses={[salesDashboardSummaryDataStatus]}
          >
            {(summary) => {
              if (summary == null) return null;
              if (summary.success === false) {
                if (summary.error.code === "CACHED_IMPACT_NOT_FOUND") {
                  return (
                    <Panel>
                      <div className="p-5 text-center">
                        <FormattedMessage
                          id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                          defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                        />
                      </div>
                    </Panel>
                  );
                }
              } else {
                const bestSellingERatedProduct = getBestSellingProduct(
                  summary.bestSellingProductData,
                  impactCategory,
                  "high"
                );
                const bestSellingARatedProduct = getBestSellingProduct(
                  summary.bestSellingProductData,
                  impactCategory,
                  "low"
                );
                return (
                  <ProductHighlights
                    highImpactProduct={bestSellingERatedProduct}
                    impactCategory={impactCategory}
                    lowImpactProduct={bestSellingARatedProduct}
                  />
                );
              }
            }}
          </StatusDisplay.Many>
        </div>
      </div>

      <div className="SalesDashboard__SalesAndImpactByTagsContainer">
        <div
          className={classNames(
            "SalesDashboardCard",
            "SalesDashboardCard_SalesAndImpactByTags",
            {
              SalesDashboardCard_loading:
                salesDashboardTagsDataStatus.type === "loading",
            }
          )}
          style={{ padding: "16px 20px 16px 24px" }}
        >
          <StatusDisplay.Many<
            [
              ProportionsChartImpactRatingInfos,
              SalesDashboardTagsData | ErrorResponse | null
            ]
          >
            statuses={[impactRatingInfosStatus, salesDashboardTagsDataStatus]}
          >
            {({ impactRatingInfos }, tagsData) => {
              if (tagsData == null) return null;
              if (tagsData.success === false) {
                if (tagsData.error.code === "CACHED_IMPACT_NOT_FOUND") {
                  return (
                    <Panel>
                      <div className="p-5 text-center">
                        <FormattedMessage
                          id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                          defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                        />
                      </div>
                    </Panel>
                  );
                }
              } else {
                const effectType = INFO_MAP[impactCategory];
                const entry = impactRatingInfos.find(
                  (r) => r.effectType === effectType
                );
                return (
                  <SalesAndAverageImpactByTags
                    impactRatingInfo={entry}
                    tags={tagsData.collections}
                  />
                );
              }
            }}
          </StatusDisplay.Many>
        </div>

        <div
          className={classNames(
            "SalesDashboardCard",
            "SalesDashboardCard_SalesAndImpactByTags",
            {
              SalesDashboardCard_loading:
                salesDashboardTagsDataStatus.type === "loading",
            }
          )}
          style={{ padding: "16px 20px 16px 24px" }}
        >
          <StatusDisplay.Many<[SalesDashboardTagsData | ErrorResponse | null]>
            statuses={[salesDashboardTagsDataStatus]}
          >
            {(tagsData) => {
              if (tagsData == null) return null;
              if (tagsData.success === false) {
                if (tagsData.error.code === "CACHED_IMPACT_NOT_FOUND") {
                  return (
                    <Panel>
                      <div className="p-5 text-center">
                        <FormattedMessage
                          id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                          defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                        />
                      </div>
                    </Panel>
                  );
                }
              } else {
                return (
                  <SalesImpactByTagsBarChart
                    tags={tagsData.collections}
                    unit={tagsData[unit]}
                  />
                );
              }
            }}
          </StatusDisplay.Many>
        </div>
      </div>
    </>
  );
};

export default ChartsForTimePeriod;

function getBestSellingProduct(
  bestSellingProducts: BestSellingProductData,
  impactCategory: ImpactCategory,
  highOrLowImpact: "high" | "low"
) {
  switch (impactCategory) {
    case ImpactCategory.GHG:
      const productsGhg = bestSellingProducts.ghg;
      return highOrLowImpact === "high"
        ? productsGhg.bestSellingERatedProduct
        : productsGhg.bestSellingARatedProduct;
    case ImpactCategory.LAND_USE:
      const productsLandUse = bestSellingProducts.landUse;
      return highOrLowImpact === "high"
        ? productsLandUse.bestSellingERatedProduct
        : productsLandUse.bestSellingARatedProduct;
    case ImpactCategory.WATER_USE:
      const productsWaterUse = bestSellingProducts.waterUse;
      return highOrLowImpact === "high"
        ? productsWaterUse.bestSellingERatedProduct
        : productsWaterUse.bestSellingARatedProduct;
  }
}
